/**
 * Staff app-switcher catalog for the left nav — which staff areas
 * (admin / billing / services / sales / support / returns) a user may switch
 * into, gated by role and per-tenant build flags. One source for both the
 * expanded chip row and the collapsed icon rail so the two never drift.
 *
 * Contains:
 * - getStaffApps — pure role→staff-app-entry builder (named export)
 *
 * Returns plain JS — no JSX.
 */
import { CurrencyExchange } from '@mui/icons-material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import MiscellaneousServicesIcon from '@mui/icons-material/MiscellaneousServices';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

/**
 * @param {object}   permissions
 * @param {boolean}  permissions.isStaff
 * @param {boolean}  permissions.isNativePlatform
 * @param {boolean}  permissions.isAccounting
 * @param {boolean}  permissions.isCrewManager
 * @param {boolean}  permissions.isSuperUser
 * @param {boolean}  permissions.isTechnician
 * @param {boolean}  permissions.isSalesOrShipping
 * @param {boolean}  permissions.isReseller
 * @param {(key: string, fallback?: string) => string} t - i18next translator.
 * @returns {{ id: string, icon: React.ComponentType, label: string, path: string }[]}
 *   The staff apps this user may access, in display order. `path` is the
 *   collapsed-rail destination; the expanded chip row ignores it.
 */
export function getStaffApps(permissions, t) {
  const {
    isStaff,
    isNativePlatform,
    isAccounting,
    isCrewManager,
    isSuperUser,
    isTechnician,
    isSalesOrShipping,
    isReseller,
  } = permissions;

  // Superusers reach every staff area (the route guards all bypass to super),
  // so each chip carries an isSuperUser escape alongside its role gate. The
  // per-tenant build flags and native-platform excludes are platform/packaging
  // constraints, not role gates, so they still apply to superusers.
  return [
    !isNativePlatform &&
      (isSuperUser || (isStaff && (isAccounting || isCrewManager))) && {
        id: 'admin',
        icon: AdminPanelSettingsIcon,
        label: t('appSelection.admin'),
        path: '/staff',
      },
    !__EXCLUDE_BILLING__ &&
      !isNativePlatform &&
      (isAccounting || isSuperUser) && {
        id: 'billing',
        icon: CurrencyExchange,
        label: t('appSelection.billing'),
        path: '/billing/',
      },
    !__EXCLUDE_SERVICES__ &&
      (isSuperUser || isCrewManager || isAccounting || isTechnician) && {
        id: 'services',
        icon: MiscellaneousServicesIcon,
        label: t('appSelection.services'),
        path: '/services/',
      },
    !__EXCLUDE_SALES__ &&
      (isSuperUser || (isStaff && isSalesOrShipping)) && {
        id: 'sales',
        icon: PointOfSaleIcon,
        label: t('appSelection.sales'),
        path: '/sales/orders',
      },
    (isSuperUser || isStaff || isReseller) && {
      id: 'support',
      icon: SupportAgentIcon,
      label: t('appSelection.support'),
      path: '/support/customers',
    },
    !__EXCLUDE_RETURNS__ &&
      !isNativePlatform &&
      (isSuperUser || isStaff) && {
        id: 'returns',
        icon: AssignmentReturnIcon,
        label: t('appSelection.returns', 'Returns'),
        path: '/rmas',
      },
  ].filter(Boolean);
}
